import { cloneDeep } from 'lodash';
import type { MouseEventHandler, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { useBuilderContext } from './BuilderContext';

export type HistoryProviderProps = PropsWithChildren;

type HistoryContextType = HistoryProviderProps & {
  cursor: number;
  stacks: {
    [id: string]: string;
  };
  stacksIds: string[];
  getCanGoBack: () => boolean;
  getCanGoNext: () => boolean;
  setNextHistory: MouseEventHandler<HTMLButtonElement>;
  setBackHistory: MouseEventHandler<HTMLButtonElement>;
};

const HistoryContext = createContext<HistoryContextType | undefined>(undefined);

export const HistoryProvider = ({ children }) => {
  const { changeCount, fullDocument, setFullDocument } = useBuilderContext();

  const [history, setHistory] = useState<{
    cursor: HistoryContextType['cursor'];
    stacks: HistoryContextType['stacks'];
    stacksIds: HistoryContextType['stacksIds'];
  }>({
    cursor: 0,
    stacks: {},
    stacksIds: [],
  });
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoNext, setCanGoNext] = useState(false);

  const handleSetHistoryStack = (cursor: HistoryContextType['cursor']) => {
    setHistory((prevState) => {
      const state = cloneDeep(prevState);
      const stackId = state.stacksIds[cursor];

      setFullDocument(JSON.parse(state.stacks[stackId]));

      setCanGoBack(
        cursor !== (cursor >= state.stacksIds.length - 1 ? cursor : cursor + 1),
      );
      setCanGoNext(cursor !== (cursor === 0 ? cursor : cursor - 1));

      return {
        ...state,
        cursor,
      };
    });
  };

  useEffect(() => {
    const handleSetHistory = (document) => {
      setHistory((prevState) => {
        const newStackId = uuid();

        const newStack = JSON.stringify({
          ...document,
          id: newStackId,
        });

        setCanGoBack(Boolean(prevState.stacksIds?.[0]));
        setCanGoNext(false);

        return {
          cursor: 0,
          stacks: {
            ...prevState.stacks,
            [newStackId]: newStack,
          },
          stacksIds: [
            newStackId,
            ...prevState.stacksIds.slice(prevState.cursor),
          ],
        };
      });
    };

    handleSetHistory(fullDocument);
  }, [changeCount, setHistory]);

  const handleNext: MouseEventHandler<HTMLButtonElement> = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newCursor =
      history.cursor === 0 ? history.cursor : history.cursor - 1;

    if (history.cursor !== newCursor) {
      handleSetHistoryStack(newCursor);
    }
  };

  const handleBack: MouseEventHandler<HTMLButtonElement> = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newCursor =
      history.cursor >= history.stacksIds.length - 1
        ? history.cursor
        : history.cursor + 1;

    if (history.cursor !== newCursor) {
      handleSetHistoryStack(newCursor);
    }
  };

  const value = useMemo(
    () => ({
      ...history,
      getCanGoBack: () => canGoBack,
      getCanGoNext: () => canGoNext,
      setNextHistory: handleNext,
      setBackHistory: handleBack,
    }),
    [history],
  );

  return (
    <HistoryContext.Provider value={value}>{children}</HistoryContext.Provider>
  );
};

export const useHistoryContext = () => {
  const context = useContext(HistoryContext);

  if (!context) {
    throw new Error('useHistoryContext must be used within HistoryProvider');
  }

  return context;
};
